// Pure geometry for the Daily Energy chart (#385) — no React, no DOM.
//
// Everything that decides WHERE a bar, a tick or a label goes lives here, so it
// is unit-tested directly instead of being inferred from rendered pixels. The
// component only measures its container and paints what these functions say.
//
// Dates are ISO `YYYY-MM-DD` strings and are treated as UTC calendar days,
// because that is what the backend buckets by (`/api/energy/daily` uses UTC
// days so its sums agree with `/api/energy/pillars` to the cent). Doing the
// arithmetic in UTC here means a viewer's own timezone can never shift a bar
// onto the neighbouring day.

/** The narrowest a day may get (bar + gap) before the chart scrolls instead. */
export const MIN_SLOT_PX = 6;
/** The widest a bar may get, however few days there are — a 3-day range should
 *  read as three bars, not three slabs. */
export const MAX_BAR_PX = 28;
/** Bar width as a share of its slot; the rest is the gap between bars. */
export const BAR_RATIO = 0.68;
/** The room one x-axis label needs ("Sep 30"), so labels never collide. */
export const MIN_LABEL_PX = 44;

const DAY_MS = 86_400_000;

const toUtcMs = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!m) return NaN;
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const fromUtcMs = (ms) => new Date(ms).toISOString().slice(0, 10);

/** Every day from `startIso` to `endIso`, both INCLUSIVE. Empty on bad input. */
export function enumerateDays(startIso, endIso) {
    const a = toUtcMs(startIso);
    const b = toUtcMs(endIso);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return [];
    const out = [];
    for (let ms = a; ms <= b; ms += DAY_MS) out.push(fromUtcMs(ms));
    return out;
}

/** Number of days in an inclusive range (0 on bad input). */
export const dayCount = (startIso, endIso) => enumerateDays(startIso, endIso).length;

/**
 * Bar layout for `n` days in `available` pixels.
 *
 * Bars share the width evenly until a slot would fall under `MIN_SLOT_PX`; past
 * that point the slot is held at the minimum and the plot grows WIDER than its
 * box, so the chart scrolls horizontally inside itself rather than squeezing
 * bars into invisibility or pushing the page sideways.
 */
export function barLayout(n, available) {
    const count = Math.max(0, Math.floor(Number(n) || 0));
    const width = Math.max(0, Number(available) || 0);
    if (count === 0) return { slot: 0, barWidth: 0, plotWidth: width, scrolls: false };

    const even = width / count;
    const slot = Math.max(MIN_SLOT_PX, even);
    const barWidth = Math.max(2, Math.min(MAX_BAR_PX, Math.floor(slot * BAR_RATIO)));
    const plotWidth = Math.max(width, slot * count);
    return { slot, barWidth, plotWidth, scrolls: even < MIN_SLOT_PX };
}

/** Show one label every `stride` days so neighbouring labels never collide. */
export function labelStride(slot, minLabel = MIN_LABEL_PX) {
    const s = Number(slot);
    if (!(s > 0)) return 1;
    return Math.max(1, Math.ceil(minLabel / s));
}

/** Indices that carry an x-axis label: every `stride`-th day from the first. */
export function labelIndices(n, stride) {
    const count = Math.max(0, Math.floor(Number(n) || 0));
    const step = Math.max(1, Math.floor(Number(stride) || 1));
    const out = [];
    for (let i = 0; i < count; i += step) out.push(i);
    return out;
}

const NICE_STEPS = [1, 2, 2.5, 5, 10];

/**
 * Y-axis ticks from 0 to a "nice" ceiling at or above `maxValue`, about
 * `target` intervals apart. An all-zero range gets a single 0 tick and a scale
 * of 0 (the component then draws every bar flat rather than dividing by zero).
 */
export function niceTicks(maxValue, target = 4) {
    const max = Number(maxValue);
    if (!(max > 0) || !Number.isFinite(max)) return { ticks: [0], max: 0 };
    const raw = max / Math.max(1, target);
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const fraction = raw / magnitude;
    const nice = NICE_STEPS.find((s) => fraction <= s) ?? 10;
    const step = nice * magnitude;
    const top = Math.ceil(max / step - 1e-9) * step;
    const ticks = [];
    // Rounded to kill float noise (0.1 + 0.2 territory) in the labels.
    for (let v = 0; v <= top + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
    return { ticks, max: ticks[ticks.length - 1] };
}

/** A bar's height as a percentage of the plot (0–100). */
export function barHeightPct(value, scaleMax) {
    const v = Number(value);
    const m = Number(scaleMax);
    if (!(v > 0) || !(m > 0)) return 0;
    return Math.min(100, (v / m) * 100);
}

/** Compact axis label: 0, 12.5, 250, 1.2k, 12k, 1.5M. Geometry only — every
 *  points figure the user READS elsewhere stays the BE's verbatim 2dp string. */
export function formatAxisValue(value) {
    const v = Number(value);
    if (!Number.isFinite(v) || v === 0) return '0';
    const abs = Math.abs(v);
    const trim = (n) => String(Number(n.toFixed(1)));
    if (abs >= 1e6) return `${trim(v / 1e6)}M`;
    if (abs >= 1e3) return `${trim(v / 1e3)}k`;
    if (abs >= 1) return String(Number(v.toFixed(1)));
    return String(Number(v.toFixed(2)));
}

/** Pixel offset (from the plot's left edge) of day `index`'s slot centre. */
export const slotCenter = (index, slot) => (index + 0.5) * slot;

/**
 * Where to scroll a scrolling plot so the day the user most likely cares about
 * — `focusIndex` (today, or the selected day) — sits inside the visible box.
 * Returns a clamped `scrollLeft`.
 */
export function scrollTargetFor(focusIndex, slot, plotWidth, viewWidth) {
    const maxScroll = Math.max(0, plotWidth - viewWidth);
    if (!(focusIndex >= 0) || !(slot > 0)) return maxScroll;
    const centre = slotCenter(focusIndex, slot);
    return Math.max(0, Math.min(maxScroll, centre - viewWidth / 2));
}
